import React from 'react';
import { getContentfulClient } from 'graphql/clients';
import { captureException } from '@sentry/react';
import { MiniArticleCard } from 'data/global/types';
import { ParselyMiniArticleCards } from 'data/parsely/ParselyQueries';
import { miniArticleCardTransformer } from 'data/global/transformer';
import { useRelatedArticleSlugsFromParsely } from './useRelatedArticleSlugsFromParsely';
import { ParselyMiniArticles as ParselyMiniArticlesType } from 'graphql/__generated__/operations/ParselyMiniArticles';
import difference from 'lodash.difference';

export const useParselyMiniArticleCards = ({
  slugsToDedupe,
  numberOfArticlesToDisplay,
  tag,
}: {
  slugsToDedupe: string[];
  numberOfArticlesToDisplay: number;
  tag?: string;
}): {
  loading: boolean;
  articles: MiniArticleCard[];
  error: boolean;
} => {
  const client = getContentfulClient(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [articles, setArticles] = React.useState<MiniArticleCard[]>([]);

  // Get personalized article slugs from Parsely
  const {
    slugs: parselySlugs,
    error: parselySlugsError,
  } = useRelatedArticleSlugsFromParsely({
    slugsToDedupe,
    numberOfArticlesToDisplay,
    tag,
  });

  React.useEffect(() => {
    if (parselySlugsError) {
      setError(true);
      setLoading(false);
    }
  }, [parselySlugsError]);

  React.useEffect(() => {
    async function getContentfulArticles(slugsToFetch: string[]) {
      try {
        const { data } = await client.query<ParselyMiniArticlesType>({
          query: ParselyMiniArticleCards,
          variables: {
            slugs: slugsToFetch,
            limit: slugsToFetch.length,
          },
          errorPolicy: 'none',
        });

        const transformedArticles = data!
          .articleCollection!.items.map(miniArticleCardTransformer)
          // Keep only the number of articles that the consumer needs to display (since we're getting extra in case of non-discoverables being returned)
          .slice(0, numberOfArticlesToDisplay);
        setArticles(transformedArticles);
      } catch (e) {
        captureException(e);
        setError(true);
      } finally {
        if (articles.length !== numberOfArticlesToDisplay) {
          setError(true);
        }
        setLoading(false);
      }
    }
    if (parselySlugs.length && !error && slugsToDedupe.length) {
      getContentfulArticles(difference(parselySlugs, slugsToDedupe));
    }
  }, [parselySlugs, error, slugsToDedupe]);

  return { loading, articles, error };
};
